import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Building2, ImageOff, Trash2, Upload } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ApiClientError, api } from "../lib/api";
import { useCompanySettings } from "../lib/useCompanySettings";
import { useSession } from "../lib/useSession";

/** Longest edge a logo is resized to before it's ever turned into a data URI
 * — a company mark doesn't need to be 4000px wide, and keeping this small is
 * what keeps the whole settings row (and every page that loads the header)
 * light, since the logo travels on every request as JSON, not as a file. */
const MAX_LOGO_DIMENSION = 512;

/** Resizes an image client-side via canvas before it's ever encoded — a raw
 * phone photo can be 10+ MB; nothing about a header mark needs that, and the
 * server-side size cap exists as a backstop, not the primary control. */
function resizeImageToDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const reader = new FileReader();
    reader.onerror = () => reject(new Error("read failed"));
    reader.onload = () => {
      img.onerror = () => reject(new Error("decode failed"));
      img.onload = () => {
        const scale = Math.min(1, MAX_LOGO_DIMENSION / Math.max(img.width, img.height));
        const w = Math.round(img.width * scale);
        const h = Math.round(img.height * scale);
        const canvas = document.createElement("canvas");
        canvas.width = w;
        canvas.height = h;
        const ctx = canvas.getContext("2d");
        if (!ctx) return reject(new Error("no canvas context"));
        ctx.drawImage(img, 0, 0, w, h);
        // PNG keeps transparency, which a logo usually wants; quality is moot
        // for PNG but harmless to pass.
        resolve(canvas.toDataURL("image/png", 0.92));
      };
      img.src = reader.result as string;
    };
    reader.readAsDataURL(file);
  });
}

/**
 * Admin-only company profile: logo, name, address, contact details. Same
 * trust boundary as Access Control (the "*" scope), reachable from the same
 * sprocket menu — org-wide branding isn't a per-module GAC concern.
 */
export default function CompanySettings() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const { user } = useSession();
  const isAdmin = user?.scopes?.includes("*") ?? false;
  const { settings, isLoading } = useCompanySettings();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [companyName, setCompanyName] = useState("");
  const [logoDataUrl, setLogoDataUrl] = useState<string | null | undefined>(undefined);
  const [addressLine1, setAddressLine1] = useState("");
  const [addressLine2, setAddressLine2] = useState("");
  const [city, setCity] = useState("");
  const [state, setState] = useState("");
  const [postalCode, setPostalCode] = useState("");
  const [country, setCountry] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [website, setWebsite] = useState("");
  const [logoError, setLogoError] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  // Seeds the form from the loaded row exactly once. Deliberately an effect,
  // not a set-state-during-render check: React treats a component setting
  // several *other* pieces of its own state mid-render as an invalid nested
  // update (error #301) once enough of them fire in the same pass — this is
  // the "sync external value into local editable state" case React's own
  // docs point at useEffect for.
  useEffect(() => {
    if (!settings || logoDataUrl !== undefined) return;
    setCompanyName(settings.companyName ?? "");
    setLogoDataUrl(settings.logoDataUrl);
    setAddressLine1(settings.addressLine1 ?? "");
    setAddressLine2(settings.addressLine2 ?? "");
    setCity(settings.city ?? "");
    setState(settings.state ?? "");
    setPostalCode(settings.postalCode ?? "");
    setCountry(settings.country ?? "");
    setPhone(settings.phone ?? "");
    setEmail(settings.email ?? "");
    setWebsite(settings.website ?? "");
  }, [settings, logoDataUrl]);

  const saveMutation = useMutation({
    mutationFn: (body: Record<string, unknown>) =>
      api("/api/v1/settings/company", { method: "PUT", body: JSON.stringify(body) }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["company-settings"] });
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    },
    onError: (err) => {
      setSaveError(err instanceof ApiClientError ? err.message : t("settings:saveFailed"));
    },
  });

  async function handleLogoPick(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = ""; // lets picking the same file twice re-trigger onChange
    if (!file) return;
    setLogoError(null);
    if (!file.type.startsWith("image/")) {
      setLogoError(t("settings:logoNotAnImage"));
      return;
    }
    try {
      const dataUrl = await resizeImageToDataUrl(file);
      setLogoDataUrl(dataUrl);
    } catch {
      setLogoError(t("settings:logoProcessFailed"));
    }
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setSaveError(null);
    saveMutation.mutate({
      companyName: companyName.trim() || null,
      logoDataUrl: logoDataUrl || null,
      addressLine1: addressLine1.trim() || null,
      addressLine2: addressLine2.trim() || null,
      city: city.trim() || null,
      state: state.trim() || null,
      postalCode: postalCode.trim() || null,
      country: country.trim() || null,
      phone: phone.trim() || null,
      email: email.trim(),
      website: website.trim(),
    });
  }

  if (!isAdmin) {
    return (
      <p className="rounded-lg border border-dashed p-8 text-center text-muted-foreground">
        {t("gac:adminOnly")}
      </p>
    );
  }

  return (
    <>
      <div className="mb-6">
        <h1>{t("settings:companySettings")}</h1>
        <p className="mt-2 text-muted-foreground">{t("settings:subtitle")}</p>
      </div>

      {!isLoading && (
        <form onSubmit={handleSubmit} className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base">
                <Building2 className="h-4 w-4" aria-hidden />
                {t("settings:companyProfile")}
              </CardTitle>
              <CardDescription>{t("settings:companyProfileHint")}</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center gap-4">
                <div className="flex h-16 w-16 shrink-0 items-center justify-center overflow-hidden rounded-lg border bg-muted">
                  {logoDataUrl ? (
                    <img
                      src={logoDataUrl}
                      alt={t("settings:logoAlt")}
                      className="h-full w-full object-contain"
                    />
                  ) : (
                    <ImageOff className="h-6 w-6 text-muted-foreground" aria-hidden />
                  )}
                </div>
                <div>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/png,image/jpeg,image/webp,image/svg+xml"
                    className="sr-only"
                    onChange={handleLogoPick}
                  />
                  <div className="flex flex-wrap gap-2">
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      onClick={() => fileInputRef.current?.click()}
                    >
                      <Upload className="h-3.5 w-3.5" aria-hidden />
                      {t("settings:uploadLogo")}
                    </Button>
                    {logoDataUrl && (
                      <Button
                        type="button"
                        size="sm"
                        variant="ghost"
                        onClick={() => setLogoDataUrl(null)}
                      >
                        <Trash2 className="h-3.5 w-3.5" aria-hidden />
                        {t("settings:removeLogo")}
                      </Button>
                    )}
                  </div>
                  <p className="mt-1 text-xs text-muted-foreground">{t("settings:logoHint")}</p>
                  {logoError && <p className="mt-1 text-xs text-destructive">{logoError}</p>}
                </div>
              </div>

              <div>
                <Label htmlFor="cs-name">{t("settings:companyName")}</Label>
                <Input
                  id="cs-name"
                  value={companyName}
                  onChange={(e) => setCompanyName(e.target.value)}
                  className="mt-1"
                  placeholder="Ninjamo Products Worldwide"
                />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-base">{t("settings:address")}</CardTitle>
            </CardHeader>
            <CardContent className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div className="sm:col-span-2">
                <Label htmlFor="cs-line1">{t("settings:addressLine1")}</Label>
                <Input
                  id="cs-line1"
                  value={addressLine1}
                  onChange={(e) => setAddressLine1(e.target.value)}
                  className="mt-1"
                />
              </div>
              <div className="sm:col-span-2">
                <Label htmlFor="cs-line2">{t("settings:addressLine2")}</Label>
                <Input
                  id="cs-line2"
                  value={addressLine2}
                  onChange={(e) => setAddressLine2(e.target.value)}
                  className="mt-1"
                />
              </div>
              <div>
                <Label htmlFor="cs-city">{t("settings:city")}</Label>
                <Input
                  id="cs-city"
                  value={city}
                  onChange={(e) => setCity(e.target.value)}
                  className="mt-1"
                />
              </div>
              <div>
                <Label htmlFor="cs-state">{t("settings:state")}</Label>
                <Input
                  id="cs-state"
                  value={state}
                  onChange={(e) => setState(e.target.value)}
                  className="mt-1"
                />
              </div>
              <div>
                <Label htmlFor="cs-postal">{t("settings:postalCode")}</Label>
                <Input
                  id="cs-postal"
                  value={postalCode}
                  onChange={(e) => setPostalCode(e.target.value)}
                  className="mt-1"
                />
              </div>
              <div>
                <Label htmlFor="cs-country">{t("settings:country")}</Label>
                <Input
                  id="cs-country"
                  value={country}
                  onChange={(e) => setCountry(e.target.value)}
                  className="mt-1"
                />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-base">{t("settings:contact")}</CardTitle>
            </CardHeader>
            <CardContent className="grid grid-cols-1 gap-4 sm:grid-cols-3">
              <div>
                <Label htmlFor="cs-phone">{t("settings:phone")}</Label>
                <Input
                  id="cs-phone"
                  type="tel"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  className="mt-1"
                />
              </div>
              <div>
                <Label htmlFor="cs-email">{t("settings:email")}</Label>
                <Input
                  id="cs-email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="mt-1"
                />
              </div>
              <div>
                <Label htmlFor="cs-website">{t("settings:website")}</Label>
                <Input
                  id="cs-website"
                  type="url"
                  value={website}
                  onChange={(e) => setWebsite(e.target.value)}
                  className="mt-1"
                  placeholder="https://ninjamo.com"
                />
              </div>
            </CardContent>
          </Card>

          <div className="flex items-center gap-3">
            <Button type="submit" disabled={saveMutation.isPending}>
              {saveMutation.isPending ? t("settings:saving") : t("settings:save")}
            </Button>
            {saved && <span className="text-sm text-muted-foreground">{t("settings:saved")}</span>}
            {saveError && <p className="text-sm text-destructive">{saveError}</p>}
          </div>
        </form>
      )}
    </>
  );
}
